import type { ReactNode } from 'react';
import { CommonUtility } from '@utility/common';
import { DateFormat, DateUtility } from '@utility/date';

/*
 * The list filter's state and the query it becomes, moved out of
 * components/Filter.tsx unchanged so the panel can be redrawn around it.
 */

export const filterTypes = {
  is: 'EQUAL',
  isNot: 'NOT_EQUAL',
} as const;

export type FilterOption = {
  key?: string | number;
  value: string | number;
  text?: string;
  flag?: string;
  image?: { src?: string };
  content?: ReactNode;
  item?: { region?: string };
};

/** One filter as a screen configures it. */
export type FilterConfig = {
  name: string;
  label?: ReactNode;
  icon?: ReactNode;
  /** `text` and `date` get their own editors; anything else lists `options`. */
  type?: string;
  options?: FilterOption[];
  multiple?: boolean;
  defaultValue?: string | number | Array<string | number>;
  /** Hidden from the panel but still part of the query. */
  skip?: boolean;
  /** Restores `defaultValue` when the user clears all filters. */
  must?: boolean;
  /** Sends the condition (`type`) under this query key instead of inverting the options. */
  typeQuery?: string;
  defaultQuery?: string;
  custom_options?: {
    value: string;
    text?: string;
    conditionImage?: ReactNode;
  }[];
  search?: boolean;
  sorted?: boolean;
  numeric?: boolean;
  placeholder?: string;
  prefix?: ReactNode;
  customSearch?: (option: FilterOption, search: string) => boolean;
};

export type FilterValue = string | number | Date | null;

/** The user's choice for one filter, as FilterButton keeps it. */
export type FilterState = {
  value?: FilterValue | FilterValue[];
  type?: string;
  options?: FilterOption[];
  filterType?: string;
  typeQuery?: string;
  toShow?: ReactNode | ReactNode[];
  skip?: boolean;
  index?: number;
};

export type FilterValues = Record<string, FilterState>;

/** Whether anything is chosen in a filter the panel shows. */
export function hasAppliedValue(values: FilterValues): boolean {
  return Object.values(values).some(state => !state.skip && !!state.value);
}

export function countApplied(values: FilterValues | undefined): number {
  let total = 0;
  for (const state of Object.values(values ?? {})) {
    if (!state.skip && Array.isArray(state.value ?? '')) {
      if ((state.value as FilterValue[]).length > 0) {
        total += 1;
      }
    } else if (state.value && !state.skip) {
      total += 1;
    }
  }
  return total;
}

function formatDay(value: FilterValue) {
  /* Dates come from the picker as Date objects; a number never reaches a date filter. */
  return DateUtility.formatDate(
    typeof value === 'number' ? String(value) : value,
    DateFormat.yearMonthDate,
  );
}

function otherOptionValues(
  state: FilterState,
  chosen: (value: unknown) => boolean,
) {
  return state.options
    ?.filter(
      option => option.value !== -1 && option.value && !chosen(option.value),
    )
    .map(option => option.value);
}

/**
 * The query the list is fetched with. "Is not" on an options filter becomes
 * the list of every other option, unless the filter sends its condition under
 * `typeQuery`.
 */
export function buildQuery(values: FilterValues): Record<string, unknown> {
  const query: Record<string, unknown> = {};

  for (const [name, state] of Object.entries(values)) {
    const { value } = state;

    if (state.filterType === 'date') {
      query[name] = Array.isArray(value)
        ? value.map(formatDay).join(',')
        : formatDay(value as FilterValue);
    } else if (Array.isArray(value)) {
      if (value[0] === '-1') {
        query[name] = '';
      } else if (state.type === filterTypes.is || state.typeQuery) {
        query[name] = value.join(',');
      } else {
        query[name] = (
          otherOptionValues(state, v => value.includes(v as FilterValue)) ?? []
        ).join(',');
      }
    } else if (state.type === filterTypes.is || state.typeQuery) {
      query[name] = value;
    } else if (!state.typeQuery) {
      query[name] = otherOptionValues(state, v => v === value)?.join(',');
    }

    if (state.typeQuery) {
      query[state.typeQuery] = state.type;
    }
  }

  return query;
}

/** Each filter's starting state, from its `defaultValue` and `defaultQuery`. */
export function initialValues(
  filters: FilterConfig[] | undefined,
): FilterValues {
  const values: FilterValues = {};

  filters?.forEach((filter, index) => {
    let value: FilterState['value'] = filter.multiple ? [] : '';
    let toShow: FilterState['toShow'] = '';

    if (filter.defaultValue) {
      value = filter.defaultValue;
      if (filter.multiple) {
        const list = Array.isArray(value) ? value : String(value).split(',');
        value = list;
        toShow = CommonUtility.isValidArray(filter.options)
          ? list
              .map(
                chosen =>
                  filter.options?.find(option => option.value === chosen)?.text,
              )
              .filter((text): text is string => text !== undefined)
          : [];
      } else if ((filter.options?.length ?? 0) > 0) {
        toShow = filter.options?.find(
          option => option.value === filter.defaultValue,
        )?.text;
      } else {
        toShow = value as ReactNode;
      }
    }

    if (filter.skip) {
      toShow = '';
    }

    values[filter.name] = {
      value,
      type: filter.defaultQuery || filterTypes.is,
      options: filter.options,
      filterType: filter.type,
      typeQuery: filter.typeQuery,
      toShow,
      skip: !!filter.skip,
      index,
    };
  });

  return values;
}

/** Clears every shown filter, keeping `must` filters at their default. */
export function clearedValues(
  filters: FilterConfig[] | undefined,
  current: FilterValues,
): FilterValues {
  const values = { ...current };

  for (const filter of filters ?? []) {
    if (!filter.skip) {
      let value: FilterState['value'] = filter.multiple ? [] : '';
      if (filter.must) {
        value = filter.defaultValue;
      }
      values[filter.name] = {
        value,
        type: filterTypes.is,
        options: filter.options,
        filterType: filter.type,
        typeQuery: filter.typeQuery,
        skip: !!filter.skip,
      };
    }
  }

  return values;
}

/** The empty state of one filter, for its Clear Filter link. */
export function clearedValue(filter: FilterConfig): FilterState {
  return { value: filter.multiple ? [] : '', type: filterTypes.is };
}

/**
 * Picking an option. In a multiple filter it toggles; picking `-1` or an
 * empty value replaces the selection. A single filter toggles off when the
 * same value is picked again.
 */
export function pickOption(
  filter: FilterConfig,
  state: FilterState,
  option: { value: FilterValue | FilterValue[]; text?: ReactNode },
  index: number,
): Partial<FilterState> {
  const { value, text } = option;

  if (filter.multiple) {
    const prevValue = (state.value as FilterValue[] | undefined) ?? [];
    const prevShow = (state.toShow as ReactNode[] | undefined) ?? [];

    if (prevValue.includes(value as FilterValue)) {
      return {
        value: prevValue.filter(x => x !== value),
        toShow: prevShow.filter(x => x !== text),
        index,
      };
    }
    if (value === -1 || !value) {
      return { value: [value as FilterValue], toShow: [text], index };
    }
    return {
      value: [...prevValue, value as FilterValue],
      toShow: [...prevShow, text],
      index,
    };
  }

  if (state.value?.toString().includes(String(value))) {
    return { value: '', toShow: '', index };
  }
  return { value, toShow: text, index };
}

/** Whether an option is currently chosen in its filter. */
export function isOptionChosen(
  filter: FilterConfig,
  state: FilterState,
  optionValue: FilterOption['value'],
): boolean {
  if (filter.multiple) {
    return ((state.value as FilterValue[] | undefined) ?? []).includes(
      optionValue,
    );
  }
  return optionValue === state.value;
}

/** Options matching the typed text, those whose value starts with it first. */
export function searchOptions(
  filter: FilterConfig,
  search: string,
): FilterOption[] {
  if (!search) {
    return filter.options ?? [];
  }

  const matches = (filter.options ?? []).filter(option =>
    filter.customSearch
      ? filter.customSearch(option, search)
      : (option.text ?? '').toLowerCase().includes(search.toLowerCase()),
  );
  /* A literal prefix, not a RegExp: a search such as `MUG(` must not throw. */
  const needle = search.toLowerCase();
  const startsWith = (value: FilterOption['value']) =>
    String(value).toLowerCase().startsWith(needle);

  return matches.sort((a, b) =>
    startsWith(a.value)
      ? startsWith(b.value)
        ? String(a.value).localeCompare(String(b.value))
        : -1
      : 1,
  );
}

/** Options by their text, as CommonUtility.sortDropdownOptions orders them. */
export function sortOptionsByText(options: FilterOption[]): FilterOption[] {
  return [...options].sort((a, b) => `${a.text}`.localeCompare(`${b.text}`));
}

/** A filter date as the kit DatePicker's YYYY-MM-DD, or '' for none. */
export function toDay(value: FilterValue | undefined): string {
  if (!value) {
    return '';
  }
  return DateUtility.formatDate(
    typeof value === 'number' ? String(value) : value,
    DateFormat.yearMonthDate,
  );
}

/**
 * The DatePicker's YYYY-MM-DD as a local-midnight Date, which is what
 * react-datepicker stored; `new Date('YYYY-MM-DD')` would be UTC midnight and
 * show the previous day west of Greenwich.
 */
export function fromDay(value: string): Date | null {
  if (!value) {
    return null;
  }
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}
